import {css} from '@mui/material';
import React from 'react';
import {useIntl} from 'react-intl';
import {useNavigate} from 'react-router-dom';

import {AlertEpisodeDispositionPostDto, AlertEpisodeRowDto, AlertSnoozeOptionDto} from './alert_types';
import {AlertCategoryIcon} from './AlertCategoryIcon';
import {AlertCriteriaRenderer} from './AlertCriteriaChips';
import {AlertEpisodeActions} from './AlertEpisodeActions';
import {AlertEpisodeTooltip} from './AlertEpisodeTooltip';
import {AlertOriginTag} from './AlertOriginTag';
import {AlertStateChip} from './AlertStateChip';
import {AlertWeightBadge} from './AlertWeightBadge';

type Props = {
  row: AlertEpisodeRowDto;
  snoozeOptions: AlertSnoozeOptionDto[];
  onSnoozeOptionsNeeded?: () => void;
  onAction: (episodeId: number, post: AlertEpisodeDispositionPostDto) => void;
  actionsDisabled?: boolean;
  systemActorName: string;
  renderCriteria?: AlertCriteriaRenderer;
  /** Where the name leads, the alert's anchor (PDW-436); a plain name without it. */
  href?: string | null;
};

/* `.row` of the design: lead column with the category icon and weight, name + meta, edge actions on hover */
const rowCss = css`
  position: relative;
  display: flex;
  gap: 11px;
  align-items: center;
  padding: 10px 10px;
  border-radius: 10px;
  min-height: 46px;
  & + & {
    margin-top: 2px;
  }
  &:hover,
  &:has(:focus-visible),
  &:has([data-name='dl-alert-episode-actions'][data-menu-open='true']) {
    background: var(--dlAlertRowHoverBackground);
  }
  & [data-name='dl-alert-episode-actions'] {
    opacity: 0;
    transition: opacity 0.12s ease;
  }
  /* shown on hover and keyboard focus (not the focus a closed menu hands back to its button after a click), and while
     the snooze menu is open: the menu is portalled out of the row */
  &:hover [data-name='dl-alert-episode-actions'],
  &:has(:focus-visible) [data-name='dl-alert-episode-actions'],
  & [data-name='dl-alert-episode-actions'][data-menu-open='true'] {
    opacity: 1;
  }
`;
const leadCss = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  width: 42px;
  flex: none;
  color: var(--dlTheme-500);
  &[data-active='false'] {
    color: var(--dlThemeGray-300);
  }
`;
const mainCss = css`
  flex: 1;
  min-width: 0;
`;
const nameCss = css`
  display: inline-block;
  max-width: 100%;
  font-family: var(--dlFontFamilySemiBold);
  font-size: 14.5px;
  line-height: 1.25;
  color: var(--dlThemeGray-800);
  overflow-wrap: anywhere;
  text-decoration-thickness: 1px;
  text-underline-offset: 2px;
  /* a link keeps the name's colour on hover and focus (the global link hover is blue) */
  &:hover,
  &:focus {
    color: var(--dlThemeGray-800);
  }
  &[data-active='false'],
  &[data-active='false']:hover,
  &[data-active='false']:focus {
    color: var(--dlThemeGray-400);
  }
  /* underlined as the alert names in the alerts list */
  &[data-link='true'] {
    text-decoration: underline;
    cursor: pointer;
  }
  &[data-state='DELETED'] {
    text-decoration: line-through;
  }
  &[data-link='true'][data-state='DELETED'] {
    text-decoration: underline line-through;
  }
`;
const metaCss = css`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  margin-top: 6px;
  min-width: 0;
  & [data-name='dl-alert-origin-tag'] {
    max-width: 180px;
  }
  &[data-active='false'] [data-name='dl-alert-origin-tag'] {
    color: var(--dlThemeGray-400);
  }
`;
const markerCss = css`
  font-family: var(--dlFontFamilyNormal);
  font-size: 11px;
  color: var(--dlThemeGray-400);
`;

/**
 * One episode of the Alerts panel (PDW-182). Left: category icon (teal when Active, grey otherwise, snoozed
 * included as the design draws it) and the
 * weight badge on Active rows only. Middle: the alert name with the episode tooltip, then the origin tag and
 * the state chip. Right, on hover: the actions the BE allows. With `href` the name is a link to the alert's anchor,
 * followed in the app as the alerts list's links are. A row whose source record no longer resolves
 * (`anchorAvailable = false`) carries a neutral marker, no link and no actions.
 */
export const AlertEpisodeRow: React.FC<Props> = (props) => {
  const {row, snoozeOptions, onSnoozeOptionsNeeded, onAction, actionsDisabled, systemActorName, renderCriteria, href} = props;
  const intl = useIntl();
  const navigate = useNavigate();
  const isActive = row.displayedState === 'ACTIVE';
  const actions = row.anchorAvailable ? row.availableActions : [];
  const link = row.anchorAvailable ? href : null;
  const nameProps = {
    'css': nameCss,
    'data-active': isActive,
    'data-state': row.displayedState,
    'data-link': Boolean(link),
    'data-name': 'dl-alert-episode-row--name',
  };
  return (
    <div css={rowCss} data-name={'dl-alert-episode-row'} data-episode-id={row.episodeId} data-state={row.displayedState}>
      <div css={leadCss} data-active={isActive}>
        <AlertCategoryIcon isGlobal={row.isGlobal} fontSize={17} />
        {isActive && <AlertWeightBadge weight={row.weight} />}
      </div>
      <div css={mainCss}>
        <AlertEpisodeTooltip
          tooltip={row.tooltip}
          displayedState={row.displayedState}
          systemActorName={systemActorName}
          renderCriteria={renderCriteria}
        >
          {link ? (
            <a
              {...nameProps}
              href={link}
              onClick={(e) => {
                e.preventDefault();
                navigate(link);
              }}
            >
              {row.name}
            </a>
          ) : (
            <span {...nameProps}>{row.name}</span>
          )}
        </AlertEpisodeTooltip>
        <div css={metaCss} data-active={isActive}>
          <AlertOriginTag component={row.component} />
          <AlertStateChip state={row.displayedState} snoozeExpiresOn={row.snoozeExpiresOn} />
          {!row.anchorAvailable && (
            <span css={markerCss} data-name={'dl-alert-episode-row--anchor-unavailable'}>
              {intl.formatMessage({id: 'alert.episode.anchorUnavailable'})}
            </span>
          )}
        </div>
      </div>
      <AlertEpisodeActions
        actions={actions}
        snoozeOptions={snoozeOptions}
        onSnoozeOptionsNeeded={onSnoozeOptionsNeeded}
        onAction={(post) => onAction(row.episodeId, post)}
        disabled={actionsDisabled}
      />
    </div>
  );
};

AlertEpisodeRow.displayName = 'AlertEpisodeRow';
